import React, { useState, useEffect, useRef } from 'react';
import { object, func } from 'prop-types';
import { get, isEmpty, size, filter, map, includes, forEach, reduce, head } from 'lodash';
import { FlexContainer, If, Icon, Row, Text, Button, HasPermissions, Loading } from '../../../../../../components/common';
import AuditOrderLegacy from '../auditOrder/variants/AuditOrderLegacy';
import { useAuditState } from '../../hooks/useAuditState';
import { GroupOrdersTabsHeader } from '../groupOrdersDrawerBody/GroupOrdersTabsHeader';
import { GroupOrdersTabContent } from '../groupOrdersDrawerBody/GroupOrdersTabContent';
import { UPDATE_AUDIT_ORDER } from '../../../../../../components/graphql/updateAuditOrder';
import { useApollo } from '../../../../../../components/contexts/ApolloContext';
import useOrderItemsQuery from '../../hooks/useOrderItemsQuery';
import styled, { css } from 'styled-components';

const FooterWrapper = styled(Row)`
    position: ${({ position }) => position};
    bottom: 0;
    width: 100%;
    left: 0;
    padding: 15px;
    border-top: 1px solid #eaeaea;
    background-color: #ffffff;
    ${({ withZindex }) => withZindex && css`
        z-index: 2000;
    `}
`;

const AuditOrderContainerLegacy = ({ selectedOrder, userInfo, onClose, refetch }) => {
    const client = useApollo();
    const [weightValidation, setWeightValidation] = useState(false);
    const [auditInProcess, setAuditInProcess] = useState(false);
    const [auditValues, setAuditValues] = useState({})
    const [state, setState] = useState({
        wasSubmitted: false,
        formValues: {},
        validationResults: null,
        currentStep: 0,
    });

    const {
        activeTab,
        setActiveTab,
        isBipickingGroup,
        orderId,
        bipickingTabs,
    } = useAuditState(selectedOrder);

    const toggleWeightValidation = () => setWeightValidation(prev => !prev);
    const { loading, data } = useOrderItemsQuery({ id: orderId });
    const firstAudit = !(size(filter(get(data, 'pickingTime.stats'), ['field', 'audit'])) > 1);
    const weightError = get(data, 'weightError', 'null');
    const semaphore = get(data, 'semaphore', 'null');

    const filterItems = (data) => {
        const { items, auditInfo } = data;
        const invalidItems = map(get(auditInfo, 'results.failed'), ({ productId }) => productId);

        return filter(items, (orderItem) => {
            return includes(invalidItems, get(orderItem, 'picking.id')) || includes(['ADDED_BY_CLIENT'], get(orderItem, 'status'));
        });
    }

    const onChangeAudits = ({ name, value }) => {
        setState({ wasSubmitted: false })

        if (get(auditValues[name], 'value', 1) !== get(value, 'value', 1)) {
            auditValues[name] = value;
            setAuditValues(auditValues);
        }
    };

    const assignProductIdValueResolver = (productId) => {
        const value = filter(auditValues, (_, index) => {
            return index === productId
        });
        return !isEmpty(value) ? head(value) : { label: 'Aprobar', value: 1 };
    }

    const formOnSubmit = (data) => {
        let values = {};

        forEach(data, item => {
            const productId = get(item, 'picking.productId', get(item, 'productId'));

            if (productId) {
                values = { ...values, [productId]: assignProductIdValueResolver(productId) };
            }
        });

        setAuditValues({});

        const validationResults = reduce(values, ({ approved, rejected }, { value }, key) =>
            ({ approved: value ? [...approved, { [key]: value }] : approved,
                rejected: !value ? [...rejected, { [key]: value }] : rejected
            }), { approved: [], rejected: [] });

        const submittedValues = reduce(values, (acc, { value }, key) => (Object.assign(acc, { [key]: value })), {});
        const hasntRejecteds = !size(get(validationResults, 'rejected'));

        setState({
            wasSubmitted: true,
            formValues: submittedValues,
            validationResults,
            currentStep: hasntRejecteds ? 0 : 1
        });
    }

    const performAudit = async () => {
        setAuditInProcess(true);

        const audits = map(state.formValues, (value, key) => ({
            productEan: key.split('-')[0],
            itemResponse: value === 1,
            userId: get(userInfo, 'email'),
            orderId
        }));

        const salesChannel =
            get(selectedOrder, 'salesChannel.id') || get(selectedOrder, 'salesChannel') || null;
        const payload = { userId: get(userInfo, 'email'), orderId, audits, salesChannel };
        const result = await client.mutate({
            mutation: UPDATE_AUDIT_ORDER,
            variables: payload
        });

        setAuditInProcess(false);
        refetch();
        if (isComponentActive.current) {
            onClose();
        }
    }

    const isComponentActive = useRef(true);

    useEffect(() => {
        isComponentActive.current = true;
        return () => {
            isComponentActive.current = false;
        };
    }, [selectedOrder?.id]);


    useEffect(() => {
        if (!state.wasSubmitted) return;
        const hasntRejecteds = !size(get(state.validationResults, 'rejected'));

        if (hasntRejecteds) {
            performAudit();
        }
    }, [state.wasSubmitted]);

    const renderOrdersWithRejected = () => {
        return <If condition={state.currentStep === 1 && size(get(state.validationResults, 'rejected'))}
            then={(
                <FlexContainer align='center' direction='column' justify='center' flex={1} >
                    <Icon name='alert-circle' size={80} color='#f38630' />
                    <FlexContainer mT={30} mB={100} direction='column' align='center'>
                        <Row justify='center'>
                            <Text text='Se rechazaron &nbsp;' />
                            <Text strong text={`${size(get(state.validationResults, 'rejected'))} productos`} />
                        </Row>
                        <Row>
                            <Text centered text='Enviaremos esta información al shopper para corregir' />
                        </Row>
                    </FlexContainer>
                    <FooterWrapper
                        noWrap
                        noFlex
                        justify='flex-end'
                        position='absolute'
                    >
                        <Button link text='btn.action.cancel' style={{ width: 110 }} onClick={() => setState({ currentStep: 0 })} />
                        <Button
                            width={107}
                            disabled={loading || auditInProcess}
                            loading={loading  || auditInProcess}
                            text='btn.action.confirm'
                            onClick={() => performAudit()}
                        />
                    </FooterWrapper>
                </FlexContainer>
            )}
        />
    }

    const renderConfirmAudit = () => {
        return <If condition={!(state.currentStep === 1 && size(get(state.validationResults, 'rejected')))}
            then={(
                <FooterWrapper
                    noWrap
                    noFlex
                    justify='flex-end'
                    position='absolute'
                >
                    <Button onClick={onClose} secondary text='Cancelar' right={10} width={107} />
                    <HasPermissions to={['audit-monitor:perform-audit']}>
                        <Button
                            onClick={() => formOnSubmit(filterItems(data))}
                            disabled={auditInProcess || (firstAudit && !weightValidation && (weightError === 'true'))}
                            loading={auditInProcess}
                            text='Auditar'
                            width={107}
                        />
                    </HasPermissions>
                </FooterWrapper>
            )}
        />
    };

    if (isBipickingGroup) {
        return <FlexContainer direction='column' style={{ padding: '0 20px' }}>
            <GroupOrdersTabsHeader
                tabs={bipickingTabs} 
                activeTab={activeTab}
                setActiveTab={setActiveTab} 
            />
            <GroupOrdersTabContent 
                data={data}
                tabsOrderData={bipickingTabs}
                userInfo={userInfo}
                activeTab={activeTab}
                onClose={onClose}
            />
        </FlexContainer>;
    };

    
    return <Loading loading={loading}>
        {!(state.currentStep === 1 && size(get(state.validationResults, 'rejected'))) && <>
            <AuditOrderLegacy
                weightError={weightError}
                semaphore={semaphore}
                firstAudit={firstAudit}
                data={data}
                onChangeAudits={onChangeAudits}
                orderSelected={selectedOrder}
                orderId={orderId}
                setWeightValidation={toggleWeightValidation}
            />
        </>}
        {renderConfirmAudit()}
        {renderOrdersWithRejected()}
    </Loading>
};

AuditOrderContainerLegacy.propTypes = {
    selectedOrder: object,
    userInfo: object,
    onClose: func.isRequired,
    refetch: func
};

export default AuditOrderContainerLegacy;